import React, { useEffect, useState, useRef } from 'react'
import './index.css';
import { getSmileWhiteImages, simmetryNotificationMail } from '../../../Services/APIs/scan';
import { postLogDetails } from "../../../Services/APIs/appDetails";
import { useLocation } from "react-router-dom"

const SmileWhiteResult = () => {
    const [selectedButton, setSelectedButton] = useState("Correction");
    const [progress, setProgress] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const [imageCropSrc, setCropImageSrc] = useState(null);
    const [imageComboSrc, setComboImageSrc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [retryCount, setRetryCount] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const containerRef = useRef(null);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const uid = searchParams.get("uid");
    
    const MAX_RETRY_ATTEMPTS = 3;
    const RETRY_DELAY = 2000;

    // Handle viewport height for iframe compatibility
    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };

        // Set initial viewport height
        setViewportHeight(window.innerHeight);
        
        // Listen for resize events
        window.addEventListener('resize', handleResize);
        
        // For iframe compatibility, also check for orientation changes
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                setViewportHeight(window.innerHeight);
            }, 100);
        });

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    const handleSliderChange = (e) => {
        setProgress(e.target.value);
    };

    const handleMouseDown = () => {
        setIsDragging(true);
    };

    // Helper function for safe image data conversion
    const safelyProcessImageData = (imageData, setImageState) => {
        if (!imageData?.data) return;

        try {
            if (typeof imageData.data === 'string') {
                setImageState(`data:image/jpeg;base64,${imageData.data}`);
                return;
            }

            const bytes = new Uint8Array(imageData.data);
            const chunkSize = 4096;
            let binary = '';

            for (let i = 0; i < bytes.byteLength; i += chunkSize) {
                const chunk = bytes.slice(i, Math.min(i + chunkSize, bytes.byteLength));
                binary += String.fromCharCode.apply(null, chunk);
            }

            const base64 = btoa(binary);
            setImageState(`data:image/jpeg;base64,${base64}`);
        } catch (err) {
            postLogDetails({
                app: "smile-white",
                payLoad: `Error processing image: ${err.message}`,
                uid
            });
            setError("Failed to process image data");
            setErrorType("PROCESSING_ERROR");
        }
    };

    // Function to load images with retry logic
    const loadImages = async (currentRetryCount = 0) => {
        if (!uid) {
            setIsLoading(false);
            setError("Missing user ID");
            setErrorType("MISSING_UID");
            return;
        }

        setIsLoading(true);
        setError(null);
        setErrorType(null);

        try {
            const res = await getSmileWhiteImages(uid);
            const { cropData, correctionData } = res.data.data;
            
            postLogDetails({ app: "smile-white", payLoad: `Response received`, uid });

            safelyProcessImageData(cropData, setCropImageSrc);
            safelyProcessImageData(correctionData, setComboImageSrc);

            postLogDetails({ app: "smile-white", payLoad: "All images processed", uid });
            setIsLoading(false);
            setRetryCount(0);
            
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Unknown error";
            const errorStatus = err.response?.status;
            
            postLogDetails({
                app: "smile-white",
                payLoad: `Error in result page: ${errorMessage}, Status: ${errorStatus}, Retry: ${currentRetryCount}`,
                uid,
            });

            if (errorStatus === 404 || errorMessage.includes("UId not found") || errorMessage.includes("not found")) {
                if (currentRetryCount < MAX_RETRY_ATTEMPTS) {
                    setError(`Images not ready yet. Retrying... (${currentRetryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
                    setErrorType("RETRY_IN_PROGRESS");
                    setRetryCount(currentRetryCount + 1);
                    
                    setTimeout(() => {
                        loadImages(currentRetryCount + 1);
                    }, RETRY_DELAY);
                } else {
                    setError("Your images are still being processed. Please try again in a few minutes.");
                    setErrorType("UID_NOT_FOUND");
                    setIsLoading(false);
                }
            } else if (errorStatus === 500) {
                setError("Server error occurred. Please try again later.");
                setErrorType("SERVER_ERROR");
                setIsLoading(false);
            } else {
                setError("Failed to load images. Please try again.");
                setErrorType("GENERAL_ERROR");
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        loadImages();
    }, [uid]);

    // Handle mouse and touch events for slider
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;

            const container = document.querySelector('.stacked-images');
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const newProgress = Math.max(0, Math.min(100, (x / rect.width) * 100));

            setProgress(newProgress);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        const handleTouchMove = (e) => {
            if (!isDragging) return;

            const container = document.querySelector('.stacked-images');
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const touch = e.touches[0];
            const x = touch.clientX - rect.left;
            const newProgress = Math.max(0, Math.min(100, (x / rect.width) * 100));

            setProgress(newProgress);
            e.preventDefault();
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging]);

    const getCurrentImageSrc = () => {
        switch (selectedButton) {
            case "Correction":
                return imageComboSrc;
            default:
                return imageComboSrc;
        }
    };

    const handleRetry = () => {
        setRetryCount(0);
        loadImages();
    };

    const renderErrorMessage = () => {
        switch (errorType) {
            case "MISSING_UID":
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <div className="text-red-500 font-jost text-lg mb-4">Invalid Request</div>
                        <div className="text-gray-600 font-jost text-sm">
                            Missing user ID. Please check your link and try again.
                        </div>
                    </div>
                );
            
            case "UID_NOT_FOUND":
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                        <div className="text-blue-500 text-4xl mb-4">⏳</div>
                        <div className="text-blue-600 font-jost text-lg mb-4">Processing In Progress</div>
                        <div className="text-gray-600 font-jost text-sm mb-4">
                            Your images are still being processed. This usually takes a few minutes.
                        </div>
                        <button
                            onClick={handleRetry}
                            className="bg-blue-500 text-white px-6 py-2 rounded-lg font-jost hover:bg-blue-600 transition-colors"
                        >
                            Check Again
                        </button>
                    </div>
                );
            
            case "RETRY_IN_PROGRESS":
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                        <div className="text-blue-500 text-4xl mb-4">🔄</div>
                        <div className="text-blue-600 font-jost text-lg mb-4">Retrying...</div>
                        <div className="text-gray-600 font-jost text-sm">
                            {error}
                        </div>
                    </div>
                );
            
            case "SERVER_ERROR":
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                        <div className="text-red-500 text-4xl mb-4">🔧</div>
                        <div className="text-red-500 font-jost text-lg mb-4">Server Error</div>
                        <div className="text-gray-600 font-jost text-sm mb-4">
                            {error}
                        </div>
                        <button
                            onClick={handleRetry}
                            className="bg-red-500 text-white px-6 py-2 rounded-lg font-jost hover:bg-red-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                );
            
            default:
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                        <div className="text-red-500 text-4xl mb-4">❌</div>
                        <div className="text-red-500 font-jost text-lg mb-4">Error</div>
                        <div className="text-gray-600 font-jost text-sm mb-4">
                            {error}
                        </div>
                        <button
                            onClick={handleRetry}
                            className="bg-gray-500 text-white px-6 py-2 rounded-lg font-jost hover:bg-gray-600 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                );
        }
    };

    return (
        <div 
            className='w-full flex flex-col'
            style={{ 
                height: `${viewportHeight}px`,
                minHeight: '100vh',
                maxHeight: '100vh',
                overflow: 'hidden'
            }}
        >
            {/* Image comparing Section */}
            <div className="flex-1 flex flex-col p-4 min-h-0">
                <div className="image-slider-container flex-1 flex flex-col min-h-0">
                    <div className="image-container flex-1 relative min-h-0">
                        <div
                            ref={containerRef}
                            className="stacked-images relative w-full h-full rounded-xl"
                            style={{ 
                                overflow: "hidden",
                                minHeight: '200px'
                            }}
                        >
                            {isLoading && errorType !== "RETRY_IN_PROGRESS" ? (
                                <div className="w-full h-full flex items-center justify-center font-jost">
                                    <div className="text-center">
                                        <div className="text-4xl mb-4">🔄</div>
                                        <div>Loading your results...</div>
                                    </div>
                                </div>
                            ) : error ? (
                                renderErrorMessage()
                            ) : (
                                <>
                                    <div className="absolute top-0 left-0 w-full h-full rounded-xl">
                                        {imageCropSrc ? (
                                            <div className="w-full h-full" style={{ position: 'relative' }}>
                                                <img
                                                    src={imageCropSrc}
                                                    alt="After transformation"
                                                    className="w-full h-full object-contain"
                                                    loading="lazy"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">Image not available</div>
                                        )}
                                    </div>

                                    <div
                                        className="before-image absolute top-0 left-0 h-full"
                                        style={{
                                            width: `${progress}%`,
                                            overflow: 'hidden',
                                            borderRight: '1px solid white'
                                        }}
                                    >
                                        {getCurrentImageSrc() && (
                                            <div className="w-full h-full" style={{ position: 'relative' }}>
                                                <img
                                                    src={getCurrentImageSrc()}
                                                    alt="Before transformation"
                                                    className="absolute top-0 left-0 h-full object-contain"
                                                    style={{
                                                        width: `${100 / (progress / 100)}%`,
                                                        maxWidth: 'none'
                                                    }}
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {!isLoading && !error && (
                        <>
                            <div className="mt-4 w-full">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={progress}
                                    onChange={handleSliderChange}
                                    className="slider w-full"
                                    style={{ "--progress": `${progress}%` }}
                                    disabled={isLoading || !!error}
                                />
                            </div>
                            
                            <div className="pt-2 flex justify-between w-full">
                                <div className='font-jost font-normal text-xs'>Before</div>
                                <div className='font-jost font-normal text-xs'>After</div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {!isLoading && !error && (
                <div className='text-center font-normal font-jost pb-4 text-[#022022] text-sm sm:text-base'>
                    Adjust Slider for Smile Options
                </div>
            )}
        </div>
    );
};

export default SmileWhiteResult;